import {
  ENTRY_ACTIVITY_IDS,
  STOP_ACTIVITY_IDS,
  TAKE_PROFIT_ACTIVITY_IDS,
} from "@shared/common/constants";
import {
  AccountAmountHistoryData,
  FixMeLater,
  FormattedChartDataPoint,
  GetAmountsHistoryResponse,
  GetTradeExtraInfoOrdersGraphRequest,
  GetTradesRequest,
  LineAnnotation,
  MultiDataset,
  OrderTypes,
  PieChartDataPoint,
  PointAnnotation,
  PriceGraphDataRequest,
  RawLineChartDataPoint,
  UnixTimestamp,
} from "@shared/types";
import { dateToMoment } from "@shared/utils/date";
import { round, toFixed } from "@shared/utils/numbers";
import { flatten, get } from "lodash";
import { AppThemeType } from "@shared/common/Themes/AppTheme";
import { GraphDataPointTypes } from "@shared/services/socket/useLiveGraphData";

export const getFormattedChartXValue = (
  timestamp: number,
  useUnixTimestamp?: boolean,
  isUnix = true
) => {
  if (useUnixTimestamp) return timestamp;
  return dateToMoment(timestamp, isUnix).format("YYYY/MM/DD HH:mm:ss");
};

export const formatTimeSeriesChartData = (
  data: RawLineChartDataPoint[],
  yValueIndex = 1,
  addChange = false,
  suffix: string | undefined | null,
  precision = 2,
  useUnixTimestamp = false
) => {
  if (!data || !data.length) return [];
  const sortedData = data.slice().sort((a, b) => a[0] - b[0]);
  const firstDataPointValue = sortedData[0][yValueIndex];

  return sortedData.reduce((acc, dataPoint) => {
    const value = dataPoint[yValueIndex];
    const formattedDataPoint: FormattedChartDataPoint = {
      x: getFormattedChartXValue(dataPoint[0], useUnixTimestamp),
      rawX: dataPoint[0],
      y: round(value, precision),
    };
    if (addChange) {
      formattedDataPoint.change = firstDataPointValue
        ? round(((value - firstDataPointValue) / firstDataPointValue) * 100, 1)
        : 0;
    }
    if (suffix) {
      formattedDataPoint.suffix = suffix;
    }
    acc.push(formattedDataPoint);
    return acc;
  }, [] as FormattedChartDataPoint[]);
};

export const consolidatePortfolioChartData = (
  portfolioHistoryData: GetAmountsHistoryResponse | undefined,
  portfolioTimeRangeFilter: string,
  portfolioClientsFilter: string[],
  exchangeToClientsMapping: Record<string, string[] | undefined>
) => {
  if (!portfolioTimeRangeFilter || !portfolioClientsFilter.length) return [];
  const clientDependency = portfolioHistoryData?.client_dependency_new;
  let accountsData: (AccountAmountHistoryData & { exchange: string })[] = [];
  Object.entries(exchangeToClientsMapping).forEach(
    ([exchangeName, clients]) => {
      clients
        ?.filter(
          (clientName) => portfolioClientsFilter.indexOf(clientName) > -1
        )
        .forEach((clientName) => {
          const accountData: AccountAmountHistoryData | undefined = get(
            portfolioHistoryData,
            ["data", exchangeName, clientName]
          );
          if (accountData) {
            accountsData.push({
              ...accountData,
              exchange: exchangeName,
            } as AccountAmountHistoryData & { exchange: string });
          }
        });
    }
  );
  const accountIds = new Set(accountsData.map((account) => account.id));
  accountsData = accountsData.filter((accountData) => {
    const accountId = accountData.id;
    const dependencyAccountIds = clientDependency?.[accountId];
    return (
      !dependencyAccountIds ||
      dependencyAccountIds.every(
        (dependencyAccountId) => !accountIds.has(dependencyAccountId)
      )
    );
  });
  const consolidatedData: RawLineChartDataPoint[] = [];
  const processedClientUuids: Set<string> = new Set();
  accountsData.forEach((accountData) => {
    const accountUuid = `${accountData.exchange}_${accountData.uuid}`;
    if (!processedClientUuids.has(accountUuid)) {
      processedClientUuids.add(accountUuid);
      const clientDataPoints = accountData[portfolioTimeRangeFilter] || [];
      clientDataPoints.forEach((newDataPoint, index) => {
        if (consolidatedData.length === index) {
          consolidatedData.push([...newDataPoint]);
        } else {
          const existingDataPoint = consolidatedData[index];
          consolidatedData[index] = existingDataPoint.map(
            (dataPointValue, index) => {
              if (index === 0) return dataPointValue;
              return dataPointValue + newDataPoint[index];
            }
          ) as RawLineChartDataPoint;
        }
      });
    }
  });
  return consolidatedData;
};

export const getActivityColor = (
  activityComponent: number,
  theme: AppThemeType
) => {
  let color = theme.colors.primaryBlue;
  let textColor = theme.colors.primaryBlue;

  if (ENTRY_ACTIVITY_IDS.includes(activityComponent)) {
    color = theme.colors.green;
    textColor = theme.colors.darkGreen;
  } else if (TAKE_PROFIT_ACTIVITY_IDS.includes(activityComponent)) {
    color = theme.colors.red;
    textColor = theme.colors.darkRed;
  } else if (STOP_ACTIVITY_IDS.includes(activityComponent)) {
    color = theme.colors.yellow;
    textColor = theme.colors.darkYellow;
  }

  return { color, textColor };
};

export const getLineAnnotation = ({
  yMax,
  color,
  distance,
  showDistanceAbove,
  orderType,
  orderNumber,
  isSimpleLine,
  text,
  isDashed,
}: {
  yMax: number;
  color: string;
  distance?: string;
  showDistanceAbove?: boolean;
  orderType?: OrderTypes;
  orderNumber?: number;
  isSimpleLine?: boolean;
  text?: string;
  isDashed?: boolean;
}): LineAnnotation => ({
  type: "line",
  yMin: yMax,
  yMax: yMax,
  borderColor: color,
  borderWidth: 1.5,
  adjustScaleRange: true,
  distance,
  showDistanceAbove: showDistanceAbove ?? false,
  orderType,
  orderNumber,
  isSimpleLine,
  text,
  isDashed,
});

export const getPointAnnotation = (
  xValue: string,
  yValue: number,
  color: string,
  timestamp?: number
): PointAnnotation => ({
  type: "point",
  xValue: xValue,
  yValue: yValue,
  backgroundColor: color,
  radius: 5,
  adjustScaleRange: false,
  timestamp: timestamp,
});

export const getChartYAxisRange = (
  data: FormattedChartDataPoint[] | undefined,
  multiData: MultiDataset[] | undefined,
  annotations: (PointAnnotation | LineAnnotation)[] | undefined,
  chartHeight: number,
  chartPaddingRatio?: number
): [number, number] => {
  const allDataPoints = multiData
    ? flatten(multiData.map((dataset) => dataset.data))
    : data;
  if (!allDataPoints?.length) return [0, 1];

  let minDataValue = Infinity;
  let maxDataValue = -Infinity;
  allDataPoints.forEach(({ y }) => {
    if (y < minDataValue) {
      minDataValue = y;
    }
    if (y > maxDataValue) {
      maxDataValue = y;
    }
  });
  annotations?.forEach((annotation) => {
    if (annotation.type !== "line") return;
    const { yMin: annotationValue } = annotation;
    if (annotationValue < minDataValue) {
      minDataValue = annotationValue;
    } else if (annotationValue > maxDataValue) {
      maxDataValue = annotationValue;
    }
  });
  [minDataValue, maxDataValue] = getChartRangeWithPadding(
    annotations,
    minDataValue,
    maxDataValue,
    chartHeight,
    chartPaddingRatio
  );
  return [minDataValue, maxDataValue];
};

export const getChartRangeWithPadding = (
  annotations: (LineAnnotation | PointAnnotation)[] | undefined,
  minYValue: number,
  maxYValue: number,
  chartHeight: number,
  chartPaddingRatio?: number
) => {
  const lineAnnotations = annotations?.filter(
    (annotation) => annotation.type === "line"
  ) as LineAnnotation[] | undefined;
  const hasClosestOrderAnnotations = lineAnnotations?.some(
    (annotation) => !!annotation.distance
  );
  const annotationPadding = ((maxYValue - minYValue) / chartHeight) * 30;
  const chartPadding = chartPaddingRatio ?? 0.002;
  let min;
  let max;
  if (
    hasClosestOrderAnnotations &&
    lineAnnotations?.find((annotation) => annotation.yMin === minYValue)
  ) {
    min = minYValue - annotationPadding;
  } else {
    const padding = chartPadding * (minYValue < 0 ? -1 : 1);
    min = minYValue * (1 - padding);
  }
  if (
    hasClosestOrderAnnotations &&
    lineAnnotations?.find((annotation) => annotation.yMin === maxYValue)
  ) {
    max = maxYValue + annotationPadding;
  } else {
    const padding = chartPadding * (maxYValue < 0 ? -1 : 1);
    max = maxYValue * (1 + padding);
  }
  return [min, max];
};

export const preprocessPieChartData = (
  data: { name: string; value: number }[],
  colors: string[],
  addOriginalValue?: boolean,
  precision?: number
) => {
  const numColors = colors.length;
  const total = data.reduce((acc, dataPoint) => acc + dataPoint.value, 0);
  const dataWithColors = data
    .slice()
    .sort((a, b) => b.value - a.value)
    .map((dataPoint, index) => {
      let data: PieChartDataPoint = {
        value: round((dataPoint.value / total) * 100, precision ?? 2),
        color: colors[index % numColors],
      };
      if (addOriginalValue) data = { ...data, originalValue: dataPoint.value };
      return {
        ...dataPoint,
        ...data,
      };
    });

  const otherValueCutoff = 1.5;
  const otherData = dataWithColors.filter(
    ({ value }) => value < otherValueCutoff
  );
  if (otherData.length < 5) return dataWithColors;

  const otherDataPoint = {
    color: "#8992A3",
    name: "Other",
    value: round(
      otherData.reduce((acc, { value }) => acc + value, 0),
      2
    ),
  };
  const dataWithOther = dataWithColors.filter(
    ({ value }) => value >= otherValueCutoff
  );
  dataWithOther.push(otherDataPoint);
  return dataWithOther;
};

export const getFormattedChartData = (
  chartData: FixMeLater,
  timeRangeFilter: string,
  clientsFilter: string[],
  exchangeToClientsMapping: Record<string, string[]>,
  suffix: string,
  yValueIndex = 1,
  precision: number
) => {
  const consolidatedData = consolidatePortfolioChartData(
    chartData,
    timeRangeFilter,
    clientsFilter,
    exchangeToClientsMapping
  );

  return formatTimeSeriesChartData(
    consolidatedData,
    yValueIndex,
    false,
    suffix,
    precision
  );
};

export const filterAnnotations = (
  data: FormattedChartDataPoint[],
  annotations?: (PointAnnotation | LineAnnotation)[],
  extendChart?: boolean,
  alwaysShowClosestOrders?: boolean
) => {
  if (extendChart || !annotations?.length) return annotations;

  const yValues = data.map((dataPoint) => dataPoint.y);
  let minMax: [number, number] = [
    yValues.length ? Math.min(...yValues) : 0,
    yValues.length ? Math.max(...yValues) : 0,
  ];

  return annotations?.filter((annotation) => {
    if (annotation.type === "line")
      return (
        (alwaysShowClosestOrders && !!annotation.distance) ||
        (annotation.yMax >= minMax[0] && annotation.yMax <= minMax[1])
      );
    return annotation;
  });
};

export const getChangeValues = (
  data?: FormattedChartDataPoint[],
  getRatio?: boolean
) => {
  if (!data || !data.length) return { value: 0, percent: 0 };

  const firstDataPointValue = data[0].y;
  const lastDataPointValue = data[data.length - 1].y;
  const changeValue = lastDataPointValue - firstDataPointValue;
  const changePercent = firstDataPointValue
    ? (changeValue / firstDataPointValue) * (getRatio ? 1 : 100)
    : 0;
  return {
    value: changeValue,
    percent: toFixed(changePercent, getRatio ? 4 : 2),
  };
};

export const updateGraphData = (
  service: any,
  endpointName: any,
  args:
    | PriceGraphDataRequest
    | GetTradeExtraInfoOrdersGraphRequest
    | GetTradesRequest,
  timeRange: string,
  currentTimestamp: UnixTimestamp,
  newDataPoint: GraphDataPointTypes
) =>
  service.util.updateQueryData(endpointName, args, (responseData: any) => {
    const graphData = responseData?.data.graph_data[timeRange];

    if (!graphData || !newDataPoint) return;

    if (currentTimestamp && graphData.length > 1) {
      const baseTimeDifference =
        graphData[1][TIMESTAMP_INDEX] - graphData[0][TIMESTAMP_INDEX];
      const currentTimeDifference =
        currentTimestamp - graphData[graphData.length - 2][TIMESTAMP_INDEX];
      const shouldReplaceLastPoint = currentTimeDifference < baseTimeDifference;
      if (shouldReplaceLastPoint) {
        graphData[graphData.length - 1] = newDataPoint;
      } else {
        graphData.push(newDataPoint);
      }
    }
  });
const TIMESTAMP_INDEX = 0;
